import React from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck, Clock3, Wrench, Refrigerator, WashingMachine, Fan, Utensils, Zap, Phone } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBraXRjaGVuJTIwYXBwbGlhbmNlc3xlbnwwfHx8fDE3ODQwODk3ODV8MA&ixlib=rb-4.1.0&q=85";
const WHY_IMG = "https://images.unsplash.com/photo-1780035206756-223e99ae10fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxhcHBsaWFuY2UlMjByZXBhaXIlMjB0ZWNobmljaWFufGVufDB8fHx8MTc4NDA4OTc4NHww&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Refrigerator, name: "Refrigerators", note: "Cooling, ice, seals, compressors" },
  { icon: WashingMachine, name: "Washers", note: "Drums, pumps, spin, leaks" },
  { icon: Fan, name: "Dryers", note: "Heating, vents, drums, belts" },
  { icon: Utensils, name: "Ovens & Stoves", note: "Igniters, elements, controls" },
  { icon: Zap, name: "Dishwashers", note: "Drains, pumps, spray arms" },
];

export default function Home() {
  return (
    <div>
      {/* ------------ HERO ------------ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-28 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="text-mono-label">EST. NYC · TRI-STATE COVERAGE</div>
            <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Broken appliance?<br />
              <span className="text-brand">We're on it.</span>
            </h1>
            <p className="mt-8 text-[#475569] text-lg max-w-2xl">
              Trades Mechanical fixes fridges, washers, dryers, ovens & dishwashers across Bronx, Westchester and Fairfield County. Same-week diagnostics, honest quotes, and warrantied repairs — texted straight to you.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
              <Link to="/book" data-testid="hero-cta-book" className="btn-brand">
                Book Repair <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:+13478806834" data-testid="hero-cta-call" className="btn-outline flex items-center gap-2">
                <Phone className="w-4 h-4" /> (347) 880-6834
              </a>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              <Stat n="24h" label="Response" />
              <Stat n="12yr" label="On the tools" />
              <Stat n="4.9★" label="Google rating" />
            </div>
          </div>

          <div className="md:col-span-4 relative hidden md:block">
            <div className="absolute -top-4 -right-4 border border-brand p-6 bg-obsidian/80 backdrop-blur-sm w-72">
              <div className="text-mono-label">Live Chat</div>
              <p className="mt-3 text-sm text-[#0F172A]">Tap the blue button bottom-right. Chat with us and we'll get right back to you — usually within an hour.</p>
              <div className="divider-slash my-4" />
              <div className="font-mono text-xs text-[#475569]">no bots · no call-centers</div>
            </div>
          </div>
        </div>

        <div className="relative border-y border-[#E2E8F0] bg-white/70 backdrop-blur">
          <Marquee gradient={false} speed={40} className="py-3">
            {["BRONX, NY", "MOUNT VERNON, NY", "YONKERS, NY", "WHITE PLAINS, NY", "SCARSDALE, NY", "GREENWICH, CT", "STAMFORD, CT", "NORWALK, CT", "DANBURY, CT", "RIDGEFIELD, CT"].map((c, i) => (
              <span key={i} className="mx-10 font-mono text-xs tracking-[0.3em] text-[#475569]">◆ {c}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* ------------ SERVICES ------------ */}
      <section id="services" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-4">
            <div className="text-mono-label">01 / What we repair</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tighter">Every major appliance.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 text-[#475569]">
            If it plugs in, spins, heats, chills or drains — we've fixed it. Trades Mechanical services every major appliance brand: GE, Whirlpool, Samsung, LG, Bosch, Sub-Zero, Viking, KitchenAid, and more.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={s.name} data-testid={`service-card-${s.name.toLowerCase().replace(/\s+/g,'-')}`} className="card-industrial p-8 relative animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-start justify-between">
                <s.icon className="w-8 h-8 text-brand" strokeWidth={2} />
                <span className="font-mono text-xs text-[#94A3B8]">0{i + 1}</span>
              </div>
              <h3 className="mt-6 font-display font-bold text-2xl">{s.name}</h3>
              <p className="mt-2 text-sm text-[#475569]">{s.note}</p>
              <Link to="/book" className="mt-6 inline-flex items-center gap-2 text-brand font-mono text-xs tracking-widest uppercase link-hover">
                Request Repair <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
          <div className="p-8 bg-brand text-white border border-brand transition-transform hover:-translate-y-1">
            <div className="text-mono-label" style={{ color: "#FFFFFF" }}>NO GUESSWORK</div>
            <h3 className="mt-6 font-display font-black text-2xl">Flat $100 diagnostic — waived when you book the repair.</h3>
            <Link to="/book" className="mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase">
              Schedule now <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* ------------ WHY US ------------ */}
      <section className="border-t border-[#E2E8F0] bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <img src={WHY_IMG} alt="Technician inspecting an appliance component" className="w-full aspect-[4/5] object-cover" />
              <div className="absolute -bottom-6 -right-6 hidden md:block bg-brand text-white p-6 w-56">
                <div className="font-mono text-xs uppercase tracking-widest">Warranty</div>
                <div className="font-display font-black text-3xl mt-2">90-day</div>
                <div className="text-xs opacity-80 mt-1">on parts and labor</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="text-mono-label">02 / Why us</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tighter">Straight talk. Solid work.</h2>
            <p className="mt-6 text-[#475569]">Trades Mechanical is a family-run repair shop — not a franchise. Chat with us and you reach the crew doing the work. When we quote, that's the price. When we fix it, it stays fixed.</p>

            <div className="mt-10 space-y-4">
              {[
                { icon: ShieldCheck, t: "90-day warranty", d: "Every repair backed. Parts and labor." },
                { icon: Clock3, t: "Same-week service", d: "Most jobs handled within 3 business days." },
                { icon: Wrench, t: "OEM-first parts", d: "Genuine manufacturer parts unless you say otherwise." },
              ].map((r) => (
                <div key={r.t} className="flex items-start gap-4 border-t border-[#E2E8F0] pt-4">
                  <r.icon className="w-6 h-6 text-brand mt-1" />
                  <div>
                    <div className="font-display font-bold text-lg">{r.t}</div>
                    <div className="text-sm text-[#475569]">{r.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ------------ CTA ------------ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="border border-[#E2E8F0] p-10 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-mono-label">Ready?</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tighter">Get your appliance back in service — this week.</h2>
            <p className="mt-4 text-[#475569]">Book a two-hour window online, or text us directly. No hold music, no phone tree.</p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/book" data-testid="cta-book" className="btn-brand justify-center">Book online</Link>
            <a href="tel:+13478806834" data-testid="cta-call" className="btn-outline text-center">(347) 880-6834</a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display font-black text-3xl text-brand">{n}</div>
      <div className="text-mono-label mt-1">{label}</div>
    </div>
  );
}
